/**
 * Authentication Helper Utilities
 * Provides utilities for checking authentication status, admin role, and token validation
 */

export interface DecodedToken {
  id?: string;
  userId?: string;
  email?: string;
  role?: string;
  name?: string;
  exp?: number;
  iat?: number;
}

/**
 * Decode JWT token without verification (client-side only)
 * Note: This does NOT verify the token signature - only decodes it
 */
export const decodeToken = (token: string): DecodedToken | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token exists and is not expired
 */
export const isTokenValid = (token: string | null): boolean => {
  if (!token) return false;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return false;
  
  // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp > currentTime;
};

/**
 * Check if user has admin role from token
 */
export const isAdminFromToken = (token: string | null): boolean => {
  if (!token) return false;
  
  const decoded = decodeToken(token);
  return decoded?.role === 'admin';
};

/**
 * Get user role from token
 */
export const getRoleFromToken = (token: string | null): string | null => {
  if (!token) return null;
  
  const decoded = decodeToken(token);
  return decoded?.role || null;
};

/**
 * Verify authentication status
 * Returns object with authentication details
 */
export const verifyAuthStatus = () => {
  const token = localStorage.getItem('authToken');
  const decoded = token ? decodeToken(token) : null;
  const isValid = isTokenValid(token);
  const isAdmin = isAdminFromToken(token);
  const role = getRoleFromToken(token);
  
  return {
    hasToken: !!token,
    token,
    decoded,
    isValid,
    isAdmin,
    role,
    isExpired: decoded && decoded.exp ? decoded.exp <= Math.floor(Date.now() / 1000) : false
  };
};

/**
 * Log authentication status for debugging
 */
export const logAuthStatus = (context: string = 'Auth Check') => {
  const status = verifyAuthStatus();
  console.log(`[${context}] Authentication Status:`, {
    hasToken: status.hasToken,
    isValid: status.isValid,
    isAdmin: status.isAdmin,
    role: status.role,
    isExpired: status.isExpired,
    tokenPreview: status.token ? `${status.token.substring(0, 20)}...` : 'No token'
  });
  return status;
};

